import { $http as defaultHTTP } from '@/api/common/Axios';
import { FetchResult } from '@/api/common/FetchResult';
import axios, { AxiosInstance } from 'axios';
import { SearchQueryFilters } from '@/models/filters/SearchQueryFilters';
import { ICustomOption } from '@/models/filters/Filters';

export interface ICrudEndpoint<DTO> {
  fetchItems(
    queryFilters: SearchQueryFilters,
    customOptions?: ICustomOption[],
  ): Promise<FetchResult<DTO>>;
  fetchItem(itemId: number | string): Promise<DTO>;
  updateItem(itemId: number | string, newItem: DTO): Promise<DTO>;
  addItem(item: DTO): Promise<DTO>;
  deleteItem(itemId: number | string): Promise<boolean>;
}

export abstract class CrudEndpoint<DTO> implements ICrudEndpoint<DTO> {
  protected entityEndpoint: string = '';
  protected $http: AxiosInstance = defaultHTTP;

  protected constructor(entityEndpoint: string) {
    this.entityEndpoint = entityEndpoint;
  }

  public async fetchItems(
    queryFilters: SearchQueryFilters,
    customOptions: ICustomOption[] = [],
  ): Promise<FetchResult<DTO>> {
    try {
      const params = queryFilters.mapQueryFilterToRequestParams();

      //hack for initial value array param
      //by default array encoded as ?initial_value[]=1, but server couldn't read "[]"
      let initialValue = '';
      if (params.initial_value) {
        //transform initial_value array into ?initial_value=1
        initialValue = params.initial_value
          .map((item: any) => `initial_value=${item}`)
          .join('&');
        //remove this param from other params
        params.initial_value = undefined;
      }
      let filterValue = '';
      if (
        queryFilters.fieldFilter?.filterValue &&
        typeof queryFilters.fieldFilter.filterValue === 'object'
      ) {
        //transform filterValue array into ?filterValue=1
        filterValue = queryFilters.fieldFilter.filterValue
          .map((item: any) => `&filter_value=${item}`)
          .join('');
      } else if (queryFilters.fieldFilter && queryFilters.fieldFilter.filterValue) {
        filterValue = `&filter_value=${queryFilters.fieldFilter.filterValue}`;
      }

      const customParams: any = {};
      if (customOptions)
        customOptions.forEach(
          option => (customParams[option.name] = option.value.toString()),
        );

      const response = await this.$http.get(
        `/${this.entityEndpoint}?${initialValue}${filterValue}`,
        {
          params: { ...customParams, ...params },
        },
      );
      return response ? response.data : [];
    } catch (err) {
      throw new Error(getCorrectErrorMessage(err));
    }
  }

  public async fetchItem(itemId: number): Promise<DTO> {
    try {
      const response = await this.$http.get(`/${this.entityEndpoint}/${itemId}`);
      return response.data;
    } catch (err) {
      throw new Error(getCorrectErrorMessage(err));
    }
  }

  public async updateItem(itemId: number, newItem: DTO): Promise<DTO> {
    try {
      const response = await this.$http.put(`/${this.entityEndpoint}/${itemId}`, newItem);
      return response.data;
    } catch (err) {
      throw new Error(getCorrectErrorMessage(err));
    }
  }

  public async addItem(item: DTO): Promise<DTO> {
    try {
      const response = await this.$http.post(`/${this.entityEndpoint}`, item);
      return response.data;
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 400 &&
        typeof err.response?.data === 'string'
      )
        throw new Error(err.response?.data);
      throw new Error(getCorrectErrorMessage(err));
    }
  }

  public async deleteItem(itemId: number | string): Promise<boolean> {
    try {
      const response = await this.$http.delete(`/${this.entityEndpoint}/${itemId}`);
      return response.data;
    } catch (err) {
      throw new Error(getCorrectErrorMessage(err));
    }
  }
}

function getCorrectErrorMessage(err: any): string {
  if (typeof err === 'string') return err;
  if (err.length > 0 && typeof err[0] === 'string') return err[0];
  if (err.error) {
    if (typeof err.error === 'string') return err.error;
    if (err.error.length > 0) return err.error[0];
  }
  if (
    err.response &&
    typeof err.response.data === 'object' &&
    err.response.data.length > 0
  )
    return err.response.data[0];
  return err.response.data.error;
}
